import { Box, Grid } from "@material-ui/core";
import MyTypography from "@saleor/components/MyTypography";
import Skeleton from "@saleor/components/Skeleton";
import { ExportPiQuery } from "@saleor/graphql";
import { makeStyles } from "@saleor/macaw-ui";
import { RelayToFlatNoEdge } from "@saleor/types";
import { countryMapping } from "@saleor/utils/countryMapping";
import classNames from "classnames";
import React from "react";
import { useIntl } from "react-intl";

const useStyles = makeStyles(
  theme => ({
    root: {
      borderRadius: 6,
      background: "white",
      boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.12)",
      overflow: "hidden",
      marginTop: "1.6rem",
      marginBottom: "1.6rem",
      width: "100%",
      [theme.breakpoints.up("md")]: {
        width: "30%"
      }
    },
    boxRight: {
      borderRadius: 6,
      background: "white",
      boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.12)",
      overflow: "hidden",
      marginTop: "1.6rem",
      marginBottom: "1.6rem",
      width: "100%",
      [theme.breakpoints.up("md")]: {
        width: "68%"
      },
      height: "max-content"
    },
    wrapper: {
      display: "flex",
      justifyContent: "space-between"
    },
    textRight: {
      textAlign: "right",
      "& .MuiTypography-root": {
        color: theme.palette.grey[900]
      }
    },
    header: {
      padding: "0.8rem 1.6rem",
      background: theme.palette.gardient.medium,
      colors: "white",
      display: "flex",
      flexDirection: "column",
      "& .MuiTypography-root": {
        color: "white"
      },
      "& .MuiTypography-caption": {
        fontSize: "1rem",
        lineHeight: "1.6rem"
      },
      "& .MuiTypography-h6": {
        lineHeight: "2.4rem"
      }
    },
    title: {
      color: theme.palette.info.main
    },
    color_800: {
      color: theme.palette.grey[800]
    },
    searchBox: {
      padding: "1.6rem"
    },
    searchBoxMargin: {
      marginTop: "2rem"
    },
    customerDetail: {
      background: theme.palette.background.default,
      borderRadius: 4,
      padding: "9px 12px",
      marginBottom: 10
    }
  }),
  { name: "ContractNoExport" }
);

export interface ViewContractPageProps {
  data: RelayToFlatNoEdge<ExportPiQuery["exportPi"]>;
}

const ContractNoExport: React.FC<ViewContractPageProps> = ({ data }) => {
  const classes = useStyles();
  const intl = useIntl();

  return (
    <>
      <div className={classes.wrapper}>
        <Box className={classes.root}>
          <Grid className={classes.header} container>
            <MyTypography variant="caption">PI No.</MyTypography>
            <MyTypography variant="h6">
              {data ? data?.code : <Skeleton />}
            </MyTypography>
          </Grid>
          <Grid className={classes.searchBox} container>
            <Grid container className={classes.customerDetail}>
              <Grid item xs={5}>
                <MyTypography variant="body3">Sold to</MyTypography>
              </Grid>
              <Grid item xs={7} className={classes.textRight}>
                <MyTypography variant="subtitle3">
                  {data ? data?.soldTo?.code : <Skeleton />}
                </MyTypography>
              </Grid>
            </Grid>
            <Grid container className={classes.customerDetail}>
              <Grid item xs={5}>
                <MyTypography variant="body3">
                  {intl.formatMessage({ defaultMessage: "ชื่อลูกค้า" })}
                </MyTypography>
              </Grid>
              <Grid item xs={7} className={classes.textRight}>
                <MyTypography variant="subtitle3">
                  <MyTypography variant="subtitle3">
                    {data ? data?.soldTo.name : <Skeleton />}
                  </MyTypography>
                </MyTypography>
              </Grid>
            </Grid>
          </Grid>
        </Box>
        <Box className={classes.boxRight}>
          <Grid className={classNames(classes.searchBox)} container>
            <Grid item xs={6}>
              <Grid container spacing={3}>
                <Grid item xs={4}>
                  <MyTypography variant="body3" className={classes.title}>
                    PO No.
                  </MyTypography>
                </Grid>
                <Grid item xs={8}>
                  <MyTypography variant="body3" className={classes.color_800}>
                    {data ? data?.poNo : <Skeleton />}
                  </MyTypography>
                </Grid>
              </Grid>
              <Grid container spacing={3}>
                <Grid item xs={4}>
                  <MyTypography variant="body3" className={classes.title}>
                    Incoterm Code
                  </MyTypography>
                </Grid>
                <Grid item xs={8}>
                  <MyTypography variant="body3" className={classes.color_800}>
                    {data ? data?.incoterm : <Skeleton />}
                  </MyTypography>
                </Grid>
              </Grid>
              <Grid container spacing={3}>
                <Grid item xs={4}>
                  <MyTypography variant="body3" className={classes.title}>
                    Payment Terms
                  </MyTypography>
                </Grid>
                <Grid item xs={8}>
                  <MyTypography variant="body3" className={classes.color_800}>
                    {data ? data?.paymentTerm : <Skeleton />}
                  </MyTypography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={6}>
              <Grid container spacing={3}>
                <Grid item xs={4}>
                  <MyTypography variant="body3" className={classes.title}>
                    Currency
                  </MyTypography>
                </Grid>
                <Grid item xs={8}>
                  <MyTypography variant="body3" className={classes.color_800}>
                    {data ? data?.currency : <Skeleton />}
                  </MyTypography>
                </Grid>
              </Grid>
              <Grid container spacing={3}>
                <Grid item xs={4}>
                  <MyTypography variant="body3" className={classes.title}>
                    Ship to country
                  </MyTypography>
                </Grid>
                <Grid item xs={8}>
                  <MyTypography variant="body3" className={classes.color_800}>
                    {data ? countryMapping(data?.shipToCountry) : <Skeleton />}
                  </MyTypography>
                </Grid>
              </Grid>
              <Grid container spacing={3}>
                <Grid item xs={4}>
                  <MyTypography variant="body3" className={classes.title}>
                    Ship to
                  </MyTypography>
                </Grid>
                <Grid item xs={8}>
                  <MyTypography variant="body3" className={classes.color_800}>
                    {data ? data?.shipToName : <Skeleton />}
                  </MyTypography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </div>
    </>
  );
};

export default ContractNoExport;
